"use client";
import KibbeOffer from "@/components/kibbe/KibbeOffer";
import KibbeConceptLinks from "@/components/kibbe/KibbeConceptLinks";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  SimpleGrid,
  Group,
  Button,
  Divider,
  Table,
  List,
  Image,
  Paper,
  ThemeIcon,
  Accordion,
  Blockquote,
} from "@mantine/core";
import { IconChevronRight, IconArrowRight, IconQuote, IconBulb, IconAlertCircle } from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import KibbeFrameworkNote from "@/components/kibbe/KibbeFrameworkNote";
import { KibbeType, kibbeTypes, kibbeComparisonContent, ComparisonContent, kibbeComparisons } from "../../../kibbeData";

interface KibbeComparisonContentProps {
  typeData: KibbeType;
  otherTypeData: KibbeType;
}

// Helper function to get comparison content (works in both directions)
function getComparisonContent(type1Id: string, type2Id: string): ComparisonContent | null {
  const key1 = `${type1Id}|${type2Id}`;
  const key2 = `${type2Id}|${type1Id}`;
  return kibbeComparisonContent[key1] || kibbeComparisonContent[key2] || null;
}

// Helper function to get canonical comparison order
function getCanonicalOrder(type1: string, type2: string): [string, string] {
  for (const [t1, t2] of kibbeComparisons) {
    if ((t1 === type1 && t2 === type2) || (t1 === type2 && t2 === type1)) {
      return [t1, t2];
    }
  }
  return type1 < type2 ? [type1, type2] : [type2, type1];
}

export default function KibbeComparisonContent({
  typeData,
  otherTypeData,
}: KibbeComparisonContentProps) {
  const content = getComparisonContent(typeData.id, otherTypeData.id);
  const [canonicalType1, canonicalType2] = getCanonicalOrder(typeData.id, otherTypeData.id);

  // Find key differences in bone structure for table
  const boneComparison = typeData.characteristics.bone.map((trait, i) => ({
    type1: trait,
    type2: otherTypeData.characteristics.bone[i] || "—",
  }));

  // Find key differences in flesh for table
  const fleshComparison = typeData.characteristics.flesh.map((trait, i) => ({
    type1: trait,
    type2: otherTypeData.characteristics.flesh[i] || "—",
  }));

  return (
    <Container size="xl" py={48}>
      <Stack gap={48}>
        {/* Hero Section */}
        <Box>
          <Text fz="xs" fw={500} tt="uppercase" c="dimmed" mb={8} style={{ letterSpacing: 2 }}>
            Kibbe Type Comparison
          </Text>
          <Title order={1} fz={{ base: 28, md: 42 }} fw={300} mb={16} style={{ letterSpacing: -1 }}>
            {typeData.name} vs {otherTypeData.name}
          </Title>
          <Text fz="lg" c="dimmed" maw={700}>
            Can&apos;t decide if you&apos;re a {typeData.name} or {otherTypeData.name}? You&apos;re
            not alone—this is one of the most common points of confusion in the Kibbe system.
          </Text>
        </Box>

        <KibbeOffer source="comparison_guide" placement="hero" type={typeData.id} otherType={otherTypeData.id} />

        <KibbeConceptLinks /><KibbeFrameworkNote />
        <SimpleGrid cols={{ base: 2, md: 2 }} spacing="md">{[typeData, otherTypeData].map(type => <Box key={type.id}><Image src={`/kibbe-outfits/${type.id}/outfit-1.webp`} alt={`${type.name} styling illustration`} h={{ base: 230, md: 430 }} fit="cover" style={{ objectPosition: "top" }} radius="md" /><Text size="sm" c="dimmed" mt="sm">{type.name} · AI outfit illustration</Text></Box>)}</SimpleGrid>

        {/* Introduction - Long form content */}
        {content && (
          <Box>
            <Text fz="md" style={{ lineHeight: 1.9 }}>
              {content.intro}
            </Text>
          </Box>
        )}

        {/* Quick Overview Cards */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
          <Paper p="xl" radius="md" withBorder>
            <Text fz="sm" fw={600} mb={8}>
              {typeData.name}
            </Text>
            <Text fz="xs" c="dimmed" mb={12}>
              {typeData.yinYang}
            </Text>
            <Text fz="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
              {typeData.tagline}. {typeData.description.split(".")[0]}.
            </Text>
            <Button
              component={Link}
              href={`/kibbe/${typeData.id}`}
              variant="subtle"
              size="xs"
              mt={16}
              rightSection={<IconChevronRight size={14} />}
            >
              Full {typeData.name} Guide
            </Button>
          </Paper>
          <Paper p="xl" radius="md" withBorder>
            <Text fz="sm" fw={600} mb={8}>
              {otherTypeData.name}
            </Text>
            <Text fz="xs" c="dimmed" mb={12}>
              {otherTypeData.yinYang}
            </Text>
            <Text fz="sm" c="dimmed" style={{ lineHeight: 1.7 }}>
              {otherTypeData.tagline}. {otherTypeData.description.split(".")[0]}.
            </Text>
            <Button
              component={Link}
              href={`/kibbe/${otherTypeData.id}`}
              variant="subtle"
              size="xs"
              mt={16}
              rightSection={<IconChevronRight size={14} />}
            >
              Full {otherTypeData.name} Guide
            </Button>
          </Paper>
        </SimpleGrid>

        <Divider />

        {/* Why These Types Get Confused */}
        {content && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>
              Why {typeData.name} and {otherTypeData.name} Get Confused
            </Title>
            <Text fz="md" c="dimmed" style={{ lineHeight: 1.9 }}>
              {content.whyConfused}
            </Text>
          </Box>
        )}

        {/* The Key Difference - Highlighted */}
        {content && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>
              <Group gap={8}>
                <IconBulb size={24} />
                The Key Difference
              </Group>
            </Title>
            <Paper p="xl" radius="md" bg="gray.0">
              <Text fz="md" style={{ lineHeight: 1.9 }}>
                {content.keyDifference}
              </Text>
            </Paper>
          </Box>
        )}

        <Divider />

        {/* Bone Structure Deep Dive */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            Frame and clothing fit
          </Title>
          {content && (
            <Text fz="md" c="dimmed" style={{ lineHeight: 1.9 }} mb={24}>
              {content.boneStructureAnalysis}
            </Text>
          )}
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{typeData.name}</Table.Th>
                <Table.Th>{otherTypeData.name}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {boneComparison.map((row, i) => (
                <Table.Tr key={i}>
                  <Table.Td>
                    <Text fz="sm">{row.type1}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm">{row.type2}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>

        {/* Flesh Analysis Deep Dive */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            Body Flesh and Curves
          </Title>
          {content && (
            <Text fz="md" c="dimmed" style={{ lineHeight: 1.9 }} mb={24}>
              {content.fleshAnalysis}
            </Text>
          )}
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{typeData.name}</Table.Th>
                <Table.Th>{otherTypeData.name}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {fleshComparison.map((row, i) => (
                <Table.Tr key={i}>
                  <Table.Td>
                    <Text fz="sm">{row.type1}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm">{row.type2}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>

        <Divider />

        {/* Quick Test Section */}
        {content && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>
              Try a fair outfit comparison
            </Title>
            <Paper p="xl" radius="md" withBorder bg="blue.0">
              <Text fz="md" style={{ lineHeight: 1.9 }}>
                {content.quickTest}
              </Text>
            </Paper>
          </Box>
        )}

        <Divider />

        {/* Celebrity Breakdown */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            Celebrity references and their limits
          </Title>
          {content && (
            <Text fz="md" c="dimmed" style={{ lineHeight: 1.9 }} mb={24}>
              {content.celebrityBreakdown}
            </Text>
          )}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
            <Box>
              <Text fz="sm" fw={600} mb={12}>
                {typeData.name} Celebrities
              </Text>
              <Group gap={8}>
                {typeData.celebrities.map((celeb, i) => (
                  <Paper key={i} p="xs" px="md" radius="md" withBorder>
                    <Text fz="xs">{celeb}</Text>
                  </Paper>
                ))}
              </Group>
            </Box>
            <Box>
              <Text fz="sm" fw={600} mb={12}>
                {otherTypeData.name} Celebrities
              </Text>
              <Group gap={8}>
                {otherTypeData.celebrities.map((celeb, i) => (
                  <Paper key={i} p="xs" px="md" radius="md" withBorder>
                    <Text fz="xs">{celeb}</Text>
                  </Paper>
                ))}
              </Group>
            </Box>
          </SimpleGrid>
        </Box>

        <Divider />

        {/* Common Mistake Section */}
        {content && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>
              <Group gap={8}>
                <IconAlertCircle size={24} />
                Common Mistakes to Avoid
              </Group>
            </Title>
            <Paper p="xl" radius="md" withBorder style={{ borderColor: "var(--mantine-color-orange-3)" }}>
              <Text fz="md" style={{ lineHeight: 1.9 }}>
                {content.commonMistake}
              </Text>
            </Paper>
          </Box>
        )}

        <Divider />

        {/* Styling Differences */}
        <Box>
          <Title order={2} fz={24} fw={400} mb={16}>
            How Styling Differs Between These Types
          </Title>
          {content && (
            <Text fz="md" c="dimmed" style={{ lineHeight: 1.9 }} mb={24}>
              {content.stylingDifference}
            </Text>
          )}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={32}>
            <Box>
              <Text fz="sm" fw={600} mb={16}>
                {typeData.name} Style Recommendations
              </Text>
              <List spacing="xs" size="sm" c="dimmed">
                {typeData.doStyles.map((style, i) => (
                  <List.Item key={i}>{style}</List.Item>
                ))}
              </List>
            </Box>
            <Box>
              <Text fz="sm" fw={600} mb={16}>
                {otherTypeData.name} Style Recommendations
              </Text>
              <List spacing="xs" size="sm" c="dimmed">
                {otherTypeData.doStyles.map((style, i) => (
                  <List.Item key={i}>{style}</List.Item>
                ))}
              </List>
            </Box>
          </SimpleGrid>
        </Box>

        <Divider />

        {/* FAQ Section - Important for Schema */}
        {content && content.faq && (
          <Box>
            <Title order={2} fz={24} fw={400} mb={16}>
              Frequently Asked Questions
            </Title>
            <Accordion variant="separated">
              {content.faq.map((item, i) => (
                <Accordion.Item key={i} value={`faq-${i}`}>
                  <Accordion.Control>
                    <Text fw={500}>{item.question}</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text fz="sm" c="dimmed" style={{ lineHeight: 1.8 }}>
                      {item.answer}
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Box>
        )}

        <Divider />

        <KibbeOffer source="comparison_guide" placement="footer" type={typeData.id} otherType={otherTypeData.id} showSample={false} />

        {/* Related Comparisons */}
        <Box>
          <Title order={3} fz={18} fw={500} mb={16}>
            Explore Other Type Comparisons
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing={12}>
            {kibbeComparisons.filter(pair => pair.includes(typeData.id) || pair.includes(otherTypeData.id)).filter(pair => !(pair.includes(typeData.id) && pair.includes(otherTypeData.id))).slice(0, 6).map(([a, b]) => <Button key={`${a}-${b}`} component={Link} href={`/kibbe/compare/${a}/${b}`} variant="default" size="sm" styles={{ label: { whiteSpace: "normal" }, root: { height: "auto", minHeight: 44 } }}>{kibbeTypes[a].name} vs {kibbeTypes[b].name}</Button>)}
          </SimpleGrid>
        </Box>
      </Stack>
    </Container>
  );
}
